import React, { useMemo } from "react";
import { Box, Chip, Typography, useTheme } from "@mui/material";
import { useIntl } from "react-intl";
import { cmmsTypeScale, getAssetHierarchyPalette, getStatLabelSx, getStatisticTileSx } from "../../cmms/assetHierarchyStyles";
import { getPriorityBadge } from "../workOrderEnums";
import { getOpenPriorityCounts } from "../workOrderPriorityCounts";
import messages from "../messages";

/*
 * How much is open and how urgent, in the page header.
 *
 * The page could already tell you how many work orders there are; it could not tell you whether
 * any of them was on fire. This is the line that answers that before a single row is read.
 *
 * It began as a strip across the top of the content, in the shape of
 * machineDetails/components/alarmBanner.jsx. It reads as a statistic tile instead because that is
 * where it ended up: beside Total in the header, where a full-width band was a wide empty slab
 * with one chip stranded in it. Hence getStatisticTileSx and no margin of its own — the header's
 * tile row owns the spacing, and a tile that adds its own sits lower than the one next to it.
 *
 * `workOrders` is deliberately the *unfiltered* list. The page's four dropdowns are answered by
 * the API, so counting what the grid fetched would empty this tile the moment someone filtered to
 * Completed — the one moment its number matters most.
 *
 * That makes the two tiles answer different questions while sitting side by side, which is a real
 * chance to mislead. The labels are what separate them: Total counts what is on screen, this one
 * says "open", which is a different population whatever the filters are set to.
 */
export default function WorkOrderPriorityTile({ workOrders }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);

  const counts = useMemo(() => getOpenPriorityCounts(workOrders), [workOrders]);

  // Nothing open means nothing to say. Returning null rather than an empty tile is what leaves a
  // quiet day's header with just the Total beside the title.
  if (counts.length === 0) return null;

  return (
    <Box sx={getStatisticTileSx(cmmsColors)}>
      <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
        {intl.formatMessage(messages.priorityBannerTitle)}
      </Typography>

      {/*
        * The chips sit where the Total tile puts its number, so the two line up down the page
        * rather than merely being the same height.
        */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
        {counts.map(({ priority, count }) => {
          // The same helper the grid's priority column renders with, so a Critical chip here is
          // the same red as the Critical badge in the row below it.
          const badge = getPriorityBadge(priority, intl);

          return (
            <Chip
              key={priority}
              size="small"
              label={intl.formatMessage(messages.priorityBannerCount, {
                count,
                priority: badge.label,
              })}
              sx={{ backgroundColor: badge.bg, color: badge.text, fontWeight: 700, height: 22 }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
